import axios from 'axios';
import Cookies from 'js-cookie';

const authHeader = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + Cookies.get('EAPISID'),
        'access-control-allow-origin': '*',
    },
});

// NOTE(NaserAlqimlas): Chunk sizes are set to 10MB
// S3 only accepts files >5MB for file uploads
const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB
const MULTIPART_THRESHOLD = 5 * 1024 * 1024; // 5MB

// NOTE(NaserAlqimlas): In the old PHP backend this is how file names
// were determined, we could come up with a better way in the future?
export const getFilePath = (originalName, basePath) => {
    const extension = originalName.split('.').pop() || '';
    const unique = `${Date.now().toString(16)}${Date.now()}`;
    return `${basePath}/${unique}.${extension}`;
};

// NOTE(NaserAlqimlas): This is a general put call abstracted for readability
export async function uploadToS3(url, file) {
    await fetch(url, {
        method: 'PUT',
        body: file,
        headers: {
            'Content-Type': file.type
        }
    });
}

export async function fileUpload(
    file,
    basePath,
    onProgress
) {
    const filePath = getFilePath(file.name, basePath);

    // NOTE(NaserAlqimlas): Due to S3 min file size needing to be > 5MB
    // for multipart uploads we need this check
    if (file.size > MULTIPART_THRESHOLD) {
        return await multipartUpload(file, filePath, onProgress);
    } else {
        return await directUpload(file, filePath);
    }
}

async function directUpload(file, filePath) {
    const response = await authHeader.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/s3/presigned_url`,
        { file_name: 'eu/' + filePath }
    );
    await uploadToS3(response.data.data.presigned_url, file);
    return filePath;
}

async function multipartUpload(
    file,
    filePath,
    onProgress
) {
    const partCount = Math.ceil(file.size / CHUNK_SIZE);
    const partLoaded = new Array(partCount).fill(0);
    const partTotal = new Array(partCount).fill(0);

    let lastTotalLoaded = 0;
    let lastTime = Date.now();
    let speedMbps = 0;

    // Step 1: Initiate
    const initRes = await authHeader.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/s3/multipart/initiate`,
        { file_name: 'eu/' + filePath, part_count: partCount }
    );
    const { upload_id, key, parts } = initRes.data.data;

    let uploadedParts;

    try {
        uploadedParts = await Promise.all(
            parts.map(
                async ({ part_number, presigned_url }) => {
                    const start = (part_number - 1) * CHUNK_SIZE;
                    const end = Math.min(start + CHUNK_SIZE, file.size);
                    const chunk = file.slice(start, end);
                    partTotal[part_number - 1] = chunk.size;

                    const etag = await uploadPart(presigned_url, chunk, (loaded) => {
                        partLoaded[part_number - 1] = loaded;

                        const totalLoaded = partLoaded.reduce((a, b) => a + b, 0);
                        const now = Date.now();
                        const elapsed = (now - lastTime) / 1000;

                        if (elapsed >= 0.5) {
                            const bytesDelta = totalLoaded - lastTotalLoaded;
                            speedMbps = bytesDelta / elapsed / (1024 * 1024);
                            lastTotalLoaded = totalLoaded;
                            lastTime = now;
                        }

                        if (onProgress) {
                            const percent = Math.round((totalLoaded / file.size) * 100);
                            const uploadedMB = totalLoaded / (1024 * 1024);
                            const totalMB = file.size / (1024 * 1024);
                            onProgress({ percent, uploadedMB, totalMB, speedMbps });
                        }
                    });

                    return { part_number, etag };
                }
            )
        );
    } catch (err) {
        await authHeader.post(
            `${process.env.REACT_APP_API_BASE_URL}/auth/s3/multipart/abort`,
            { upload_id, key }
        );
        throw err;
    }

    await authHeader.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/s3/multipart/complete`,
        { upload_id, key, parts: uploadedParts }
    );

    return key.replace(/^eu\//, '');
}


function uploadPart(
    presignedUrl,
    chunk,
    onProgress
) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', presignedUrl);

        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
                onProgress(e.loaded, e.total);
            }
        });

        xhr.addEventListener('load', () => {
            const etag = xhr.getResponseHeader('ETag')?.replaceAll('"', '');
            if (!etag) return reject(new Error('Missing ETag in response'));
            resolve(etag);
        });

        xhr.addEventListener('error', () => reject(new Error('Part upload failed')));
        xhr.send(chunk);
    });
}
